import { ArrowLeftRight, Ellipsis, History, MapPin, SlidersHorizontal } from 'lucide-react'
import { ReactNode } from 'react'

import { StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { cn } from '@/lib/utils'
import { formatQuantity, stockLevelBadge, stockLevelOf } from '@/lib/warehouse/stockLevels'

import { StockAdjustPopover } from './StockAdjustPopover'

export type StockItemsView = 'byWarehouse' | 'bySku' | 'byProduct'

export interface StockColumns {
  sku: boolean
  warehouse: boolean
  batch: boolean
}

interface StockItemRowProps {
  stockItem?: StockItem
  columns: StockColumns
  skuLabel?: (stockItem: StockItem) => ReactNode
  isSelected?: boolean
  onSelectSku?: (skuId: string) => void
  onAdjusted?: () => void
  onTransfer?: (stockItem: StockItem) => void
  onEditBinLocation?: (stockItem: StockItem) => void
  onShowMoves?: (stockItem: StockItem) => void
}

export const StockItemRow = ({
  stockItem,
  columns,
  skuLabel,
  isSelected,
  onSelectSku,
  onAdjusted,
  onTransfer,
  onEditBinLocation,
  onShowMoves,
}: StockItemRowProps) => {
  const span = 5 + Number(columns.sku) + Number(columns.warehouse) + Number(columns.batch)

  if (!stockItem) {
    return (
      <TableRow className="hover:bg-transparent">
        {Array.from({ length: span }).map((_, i) => (
          <TableCell key={i} className={cn(i !== 0 && i !== span - 3 && i !== span - 1 && 'hidden md:table-cell')}>
            <Skeleton className="h-4 w-full max-w-24" />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const level = stockLevelOf(stockItem)
  const badge = level === 'out' || level === 'low' ? stockLevelBadge[level] : null
  const unlimited = stockItem.isUnlimited
  const mobileDetail = [
    columns.sku && columns.warehouse ? stockItem.warehouseName : null,
    stockItem.binLocation ? `lokace ${stockItem.binLocation}` : null,
    stockItem.batchNumber ? `šarže ${stockItem.batchNumber}` : null,
    !unlimited ? `skladem ${formatQuantity(stockItem.quantityOnHand)}` : null,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <TableRow
      className={cn('group', onSelectSku && 'cursor-pointer')}
      data-state={isSelected ? 'selected' : undefined}
      onClick={onSelectSku ? () => onSelectSku(stockItem.skuId) : undefined}
    >
      {columns.sku && (
        <TableCell className="max-w-0 w-full">
          {skuLabel ? (
            skuLabel(stockItem)
          ) : (
            <>
              <p className="truncate font-medium md:min-w-48">{stockItem.skuName || stockItem.skuCode}</p>
              {stockItem.skuCode && !stockItem.skuCode.startsWith('pr_') && (
                <p className="truncate text-xs text-muted-foreground">{stockItem.skuCode}</p>
              )}
            </>
          )}
          {mobileDetail && <p className="truncate text-xs text-muted-foreground md:hidden">{mobileDetail}</p>}
        </TableCell>
      )}
      {columns.warehouse && (
        <TableCell className={cn(!columns.sku && 'w-full max-w-0 md:max-w-none', columns.sku && 'hidden md:table-cell')}>
          <p className="truncate md:overflow-visible">{stockItem.warehouseName}</p>
          {stockItem.warehouseCode && <p className="text-xs text-muted-foreground">{stockItem.warehouseCode}</p>}
          {!columns.sku && mobileDetail && (
            <p className="truncate text-xs text-muted-foreground md:hidden">{mobileDetail}</p>
          )}
        </TableCell>
      )}
      <TableCell className="hidden text-muted-foreground md:table-cell">{stockItem.binLocation || '-'}</TableCell>
      {columns.batch && <TableCell className="hidden text-muted-foreground md:table-cell">{stockItem.batchNumber || '-'}</TableCell>}
      <TableCell className="hidden text-right text-muted-foreground md:table-cell">
        {unlimited ? '-' : formatQuantity(stockItem.quantityReserved)}
      </TableCell>
      <TableCell className="text-right">
        {unlimited ? (
          <span className="text-muted-foreground">Neomezeno</span>
        ) : (
          <span className="inline-flex items-center justify-end gap-2">
            {badge && <StatusBadge status={badge.status} label={badge.label} />}
            <span className={cn('font-medium', level === 'out' && 'text-danger')}>
              {formatQuantity(stockItem.quantityAvailable)}
            </span>
          </span>
        )}
      </TableCell>
      <TableCell className="hidden text-right md:table-cell">
        {unlimited ? <span className="text-muted-foreground">-</span> : formatQuantity(stockItem.quantityOnHand)}
      </TableCell>
      <TableCell className="w-px pl-0 text-right">
        <div className="flex items-center justify-end gap-1">
          <StockAdjustPopover stockItem={stockItem} onSuccess={onAdjusted}>
            <Button variant="ghost" size="sm" aria-label="Upravit">
              <SlidersHorizontal />
              <span className="hidden md:inline">Upravit</span>
            </Button>
          </StockAdjustPopover>
          {(onTransfer || onEditBinLocation || onShowMoves) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon-sm" aria-label="Další akce">
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {onTransfer && (
                  <DropdownMenuItem onClick={() => onTransfer(stockItem)}>
                    <ArrowLeftRight />
                    Přesunout do jiného skladu
                  </DropdownMenuItem>
                )}
                {onEditBinLocation && (
                  <DropdownMenuItem onClick={() => onEditBinLocation(stockItem)}>
                    <MapPin />
                    Změnit lokaci
                  </DropdownMenuItem>
                )}
                {onShowMoves && (
                  <DropdownMenuItem onClick={() => onShowMoves(stockItem)}>
                    <History />
                    Zobrazit pohyby
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
